import React, { useState, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import Papa from "papaparse";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Typography,
  Box,
  Pagination,
  Alert,
  Select,
  MenuItem,
} from "@mui/material";
import { NotificationManager } from "react-notifications";
import { Upload } from "lucide-react";
import { bulkUploadRoutingGuideRecords } from "../../Services/RoutingGuidesDetails";

const ROWS_PER_PAGE = 10;

const REQUIRED_FIELDS = [
  "ship_from_zip1",
  "ship_from_zip2",
  "carrier",
  "dc",
  "consolidator",
  "service_level",
  "store",
  "department",
  "u_min",
  "u_max",
  "wt_min",
  "wt_max",
  "unit_type",
];

const DROPDOWN_FIELDS = {
  consolidator: "consolidators",
  dc: "dcs",
  carrier: "carriers",
  service_level: "service_levels",
  store: "stores",
  unit_type: "unit_types",
};

const MOCK_DATA = {
  unit_types: [
    { id: "Carton", name: "Carton" },
    { id: "N/A", name: "N/A" },
    { id: "Pallet", name: "Pallet" },
    { id: "$ Value at Cost", name: "$ Value at Cost" },
  ],
};

const BulkUploadSection = ({
  commodityCodes,
  consolidators,
  dcs,
  carriers,
  service_levels,
  stores,
  guideId,
  onUploadComplete,
  supplierdetail,
}) => {
  const [open, setOpen] = useState(false);
  const [rawRecords, setRawRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showValidationWarning, setShowValidationWarning] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [columnErrors, setColumnErrors] = useState([]);

  const dropdownOptions = {
    consolidators: consolidators.map((item) => ({
      id: item.facility_id,
      name: item.name,
    })),
    dcs: dcs.map((item) => ({
      id: item.facility_id,
      name: item.name,
    })),
    carriers: carriers.map((item) => ({
      id: item.scac_id,
      name: item.scac_name,
    })),
    service_levels: service_levels.map((item) => ({
      id: item.service_level_id,
      name: item.service_level,
    })),
    stores: stores?.length ? stores : MOCK_DATA.stores,
    unit_types: MOCK_DATA.unit_types,
  };

  const isEmptyValue = (value) =>
    value === undefined || value === null || value.toString().trim() === "";

  const getMissingFieldsCount = (record) =>
    REQUIRED_FIELDS.reduce(
      (count, field) => count + (isEmptyValue(record[field]) ? 1 : 0),
      0
    );

  const sortedRecords = useMemo(() => {
    return [...rawRecords].sort((a, b) => {
      const aMissingFields = getMissingFieldsCount(a);
      const bMissingFields = getMissingFieldsCount(b);
      return bMissingFields - aMissingFields;
    });
  }, [rawRecords]);

  const currentRecords = sortedRecords.slice(
    (page - 1) * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE
  );

  const recordsWithMissingFields = useMemo(
    () =>
      sortedRecords.filter((record) => getMissingFieldsCount(record) > 0)
        .length,
    [sortedRecords]
  );

  const validateColumns = (headers) => {
    const missingRequiredColumns = REQUIRED_FIELDS.filter(
      field => !headers.includes(field)
    );
    
    if (missingRequiredColumns.length > 0) {
      setColumnErrors(missingRequiredColumns);
      NotificationManager.error(
        `Missing required columns: ${missingRequiredColumns.join(", ")}`,
        "Validation Error"
      );
      return false;
    }
    return true;
  };

  const validateRecord = (record) => {
    const missingFields = REQUIRED_FIELDS.filter(field => 
      isEmptyValue(record[field])
    );
    return missingFields.length === 0;
  };

  const onDropHandler = (files) => {
    const file = files[0];
    if (!file) return;

    setIsLoading(true);
    setShowValidationWarning(false);
    setColumnErrors([]);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (!validateColumns(Object.keys(results.data[0] || {}))) {
          setIsLoading(false);
          setRawRecords([]);
          return;
        }

        const parsedRecords = results.data.filter((record) =>
          Object.values(record).some((value) => !isEmptyValue(value))
        );

        const invalidRecords = parsedRecords.filter(
          record => !validateRecord(record)
        );

        if (invalidRecords.length > 0) {
          setShowValidationWarning(true);
          NotificationManager.warning(
            `${invalidRecords.length} record(s) have missing required fields`,
            "Validation Warning"
          );
        }

        setRawRecords(parsedRecords);
        setIsLoading(false);
        setPage(1);
      },
      error: (error) => {
        console.error("Error parsing CSV:", error);
        NotificationManager.error("Failed to parse CSV file");
        setIsLoading(false);
      },
    });
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "text/csv": [".csv"],
    },
    onDrop: onDropHandler,
    multiple: false,
  });

  const isUploadDisabled = useMemo(() => {
    return (
      rawRecords.length === 0 ||
      isLoading ||
      recordsWithMissingFields > 0 ||
      columnErrors.length > 0
    );
  }, [rawRecords.length, isLoading, recordsWithMissingFields, columnErrors]);

  const handleClose = () => {
    setOpen(false);
    setRawRecords([]);
    setPage(1);
    setShowValidationWarning(false);
    setUploadProgress(0);
  };

  const mapToIdBeforeUpload = (record, dropdownOptions) => {
    const mappedRecord = { ...record };

    Object.entries(DROPDOWN_FIELDS).forEach(([field, optionKey]) => {
      if (record[field]) {
        const options = dropdownOptions[optionKey];
        const matchById = options.find(
          (opt) => opt.id?.toString() === record[field]?.toString()
        );

        if (matchById) {
          mappedRecord[field] = matchById.id;
        } else {
          const matchByName = options.find(
            (opt) =>
              opt.name?.toLowerCase() ===
              record[field]?.toString()?.toLowerCase()
          );
          if (matchByName) {
            mappedRecord[field] = matchByName.id;
          }
        }
      }
    });

    return mappedRecord;
  };

  const handleUpload = async () => {
    if (recordsWithMissingFields > 0) {
      NotificationManager.error(
        `Cannot upload: ${recordsWithMissingFields} record(s) have missing required fields`
      );
      return;
    }

    const mappedRecords = sortedRecords.map((record) =>
      mapToIdBeforeUpload(record, dropdownOptions)
    );

    try {
      setIsLoading(true);
      setUploadProgress(50);

      await bulkUploadRoutingGuideRecords(guideId, mappedRecords);

      setUploadProgress(100);
      await onUploadComplete();

      NotificationManager.success("Upload complete");
      handleClose();
    } catch (error) {
      console.error("Upload failed:", error);
      NotificationManager.error("Upload failed");
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
    }
  };

  const updateRecord = (index, field, value) => {
    const actualIndex = (page - 1) * ROWS_PER_PAGE + index;
    const newRecords = [...rawRecords];

    const recordToUpdate = sortedRecords[actualIndex];
    const originalIndex = rawRecords.findIndex((record) => {
      return Object.entries(record).every(([key, val]) => {
        if (key === field) return true;
        return val === recordToUpdate[key];
      });
    });

    if (originalIndex !== -1) {
      newRecords[originalIndex] = {
        ...newRecords[originalIndex],
        [field]: value,
      };
      setRawRecords(newRecords);

      const hasMissingFields = newRecords.some(
        (record) => getMissingFieldsCount(record) > 0
      );
      setShowValidationWarning(hasMissingFields);
    }
  };

  const isFieldMissing = (record, field) => {
    return REQUIRED_FIELDS.includes(field) && isEmptyValue(record[field]);
  };

  const getColumnWidth = (header) => {
    switch (header) {
      case "ship_from_zip1":
      case "ship_from_zip2":
        return "120px";
      case "u_min":
      case "u_max":
      case "wt_min":
      case "wt_max":
        return "100px";
      case "carrier":
      case "consolidator":
      case "dc":
      case "service_level":
      case "store":
      case "department":
        return "200px";
      case "ship_message":
      case "contact":
        return "200px";
      default:
        return "150px";
    }
  };

  const renderCellContent = (record, key, value, idx, isMissing) => {
    if (key === "store" || key === "department") {
      return (
        <Box
          sx={{
            border: '1px solid #ddd',
            borderRadius: '4px',
            p: 1,
            backgroundColor: isMissing ? "rgba(255, 0, 0, 0.05)" : "white",
            minHeight: "36px",
          }}
        >
          <TextField
            value={value || ""}
            onChange={(e) => updateRecord(idx, key, e.target.value)}
            size="small"
            fullWidth
            error={isMissing}
            sx={{
              "& .MuiOutlinedInput-root": {
                border: "none",
                "&:hover fieldset": {
                  border: "none",
                },
                "&.Mui-focused fieldset": {
                  border: "none",
                },
              },
              "& fieldset": {
                border: "none",
              },
            }}
          />
        </Box>
      );
    }

    if (DROPDOWN_FIELDS[key]) {
      const options = dropdownOptions[DROPDOWN_FIELDS[key]];
      return (
        <Select
          value={value || ""}
          onChange={(e) => updateRecord(idx, key, e.target.value)}
          size="small"
          fullWidth
          error={isMissing}
          sx={{
            height: "36px",
            backgroundColor: isMissing ? "rgba(255, 0, 0, 0.05)" : "inherit",
          }}
        >
          <MenuItem value="">
            <em>Select {key.replace(/_/g, " ")}</em>
          </MenuItem>
          {options.map((option) => (
            <MenuItem key={option.id} value={option.name}>
              {option.name}
            </MenuItem>
          ))}
        </Select>
      );
    }

    return (
      <TextField
        value={value || ""}
        onChange={(e) => updateRecord(idx, key, e.target.value)}
        size="small"
        fullWidth
        error={isMissing}
        sx={{
          "& .MuiOutlinedInput-root": {
            height: "36px",
            backgroundColor: isMissing ? "rgba(255, 0, 0, 0.05)" : "inherit",
          },
        }}
      />
    );
  };


  return (
    <>
      <Button
        variant="contained"
        onClick={() => setOpen(true)}
        sx={{ backgroundColor: "#3f506b" }}
      >
        Bulk Upload
      </Button>
  
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "90vw",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="h6">Bulk Upload Records</Typography>
            {showValidationWarning && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {recordsWithMissingFields} record(s) have missing required fields
              </Alert>
            )}
          </Box>
        </DialogTitle>
  
        <DialogContent>
          {rawRecords.length === 0 ? (
            <Box
              {...getRootProps()}
              sx={{
                border: "2px dashed #ccc",
                p: 4,
                textAlign: "center",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              <input {...getInputProps()} />
              <Upload size={40} />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Drop CSV file or click to upload
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: "calc(90vh - 200px)" }}>
                <Table stickyHeader sx={{ tableLayout: "fixed" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          backgroundColor: "#f5f5f5",
                          fontWeight: "bold",
                        }}
                      >
                        Sl No
                      </TableCell>
                      {Object.keys(currentRecords[0] || {}).map((header) => (
                        <TableCell
                          key={header}
                          sx={{
                            width: getColumnWidth(header),
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            backgroundColor: "#f5f5f5",
                            fontWeight: "bold",
                          }}
                        >
                          {header.replace(/_/g, " ").toUpperCase()}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentRecords.map((record, idx) => (
                      <TableRow
                        key={idx}
                        sx={{
                          backgroundColor: getMissingFieldsCount(record) > 0
                            ? "rgba(255, 0, 0, 0.05)"
                            : "inherit",
                        }}
                      >
                        <TableCell>{(page - 1) * ROWS_PER_PAGE + idx + 1}</TableCell>
                        {Object.entries(record).map(([key, value]) => (
                          <TableCell
                            key={key}
                            sx={{
                              width: getColumnWidth(key),
                              padding: "8px",
                            }}
                          >
                            {renderCellContent(
                              record,
                              key,
                              value,
                              idx,
                              isFieldMissing(record, key)
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
  
              <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                <Pagination
                  count={Math.ceil(sortedRecords.length / ROWS_PER_PAGE)}
                  page={page}
                  onChange={(_, newPage) => setPage(newPage)}
                />
              </Box>
            </>
          )}
        </DialogContent>
  
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={isUploadDisabled}
            sx={{ backgroundColor: "#3f506b" }}
          >
            {isLoading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default BulkUploadSection;